import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Extend jsPDF with autoTable type
declare module 'jspdf' {
    interface jsPDF {
        autoTable: any;
    }
}

export interface ReportIssue {
    id: string;
    type: string;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    file: string;
    line: number;
    message: string;
    suggestion?: string;
}

export interface ReportData {
    project: {
        name: string;
        version: string;
        date: string;
        commit: string;
        description?: string;
        repositoryUrl?: string;
    };
    metrics: {
        coverage: number;
        bugs: number;
        technicalDebt: string;
        vulnerabilities: number;
        codeSmells: number;
        healthScore: number;
    };
    issues?: ReportIssue[];
    summary?: string;
}

export const generatePdfReport = (data: ReportData) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const COLORS = {
        PRIMARY: '#0E1111',
        SECONDARY: '#6366F1',
        SUCCESS: '#10B981',
        ERROR: '#EF4444',
        WARNING: '#F59E0B',
        INFO: '#3B82F6',
        BG_LIGHT: '#F8FAFC',
        BORDER: '#E2E8F0',
        TEXT_MAIN: '#1E293B',
        TEXT_MUTED: '#64748B'
    };

    const margin = 20;
    const pageWidth = 210;
    const contentWidth = pageWidth - (margin * 2);

    // --- PAGE 1: COVER ---
    // Background accent
    doc.setFillColor(COLORS.PRIMARY);
    doc.rect(0, 0, pageWidth, 15, 'F');
    
    // Logo / Brand
    doc.setFont('courier', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(COLORS.PRIMARY);
    doc.text('DEVLINT', margin, 40);
    doc.setLineWidth(1);
    doc.line(margin, 43, margin + 40, 43);

    // Report Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(42);
    doc.text('Technical', margin, 80);
    doc.text('Analysis', margin, 95);
    doc.text('Report', margin, 110);

    // Project Info
    doc.setFontSize(14);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('PREPARED FOR:', margin, 140);
    doc.setTextColor(COLORS.PRIMARY);
    doc.setFontSize(22);
    doc.text(data.project.name.toUpperCase(), margin, 150);

    // Divider
    doc.setDrawColor(COLORS.BORDER);
    doc.line(margin, 165, pageWidth - margin, 165);

    // Metadata Grid
    doc.setFontSize(10);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('REPORT DATE', margin, 180);
    doc.text('VERSION', margin + 60, 180);
    doc.text('COMMIT/REF', margin + 110, 180);

    doc.setTextColor(COLORS.PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(new Date(data.project.date).toLocaleDateString(), margin, 187);
    doc.text(data.project.version || 'v1.0.0', margin + 60, 187);
    doc.text(data.project.commit.substring(0, 12), margin + 110, 187);

    // Footer Cover
    doc.setFont('courier', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('© 2026 DEVLINT AI ENGINE. ALL RIGHTS RESERVED.', pageWidth / 2, 280, { align: 'center' });

    // --- PAGE 2: SUMMARY & METRICS ---
    doc.addPage();
    let currentY = 30;

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(COLORS.PRIMARY);
    doc.text('1. EXECUTIVE SUMMARY', margin, currentY);
    currentY += 10;

    // Health Score Gauge (Simple Representation)
    const gaugeWidth = contentWidth;
    const gaugeHeight = 12;
    doc.setFillColor(COLORS.BORDER);
    doc.roundedRect(margin, currentY, gaugeWidth, gaugeHeight, 2, 2, 'F');
    
    const score = data.metrics.healthScore || 0;
    const scoreColor = score > 80 ? COLORS.SUCCESS : score > 50 ? COLORS.WARNING : COLORS.ERROR;
    doc.setFillColor(scoreColor);
    doc.roundedRect(margin, currentY, (gaugeWidth * score) / 100, gaugeHeight, 2, 2, 'F');
    
    currentY += 8;
    doc.setFontSize(9);
    doc.setTextColor(COLORS.TEXT_MAIN);
    doc.text(`HEALTH SCORE: ${score.toFixed(1)}%`, margin, currentY + 10);
    currentY += 18;

    // Summary Text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(COLORS.TEXT_MAIN);
    const summaryText = data.summary || data.project.description || 'No automated summary available.';
    const splitSummary = doc.splitTextToSize(summaryText, contentWidth);
    doc.text(splitSummary, margin, currentY);
    currentY += (splitSummary.length * 6) + 15;

    // KPI Grid
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('2. KEY PERFORMANCE INDICATORS', margin, currentY);
    currentY += 10;

    const kpis = [
        { label: 'BUGS FOUND', value: data.metrics.bugs.toString(), color: COLORS.ERROR },
        { label: 'VULNERABILITIES', value: data.metrics.vulnerabilities.toString(), color: COLORS.WARNING },
        { label: 'CODE SMELLS', value: data.metrics.codeSmells.toString(), color: COLORS.INFO },
        { label: 'TECH DEBT', value: data.metrics.technicalDebt, color: COLORS.PRIMARY },
        { label: 'COVERAGE', value: `${data.metrics.coverage}%`, color: COLORS.SUCCESS },
    ];

    const kpiBoxWidth = (contentWidth - 10) / 2;
    kpis.forEach((kpi, i) => {
        const x = margin + (i % 2 === 0 ? 0 : kpiBoxWidth + 10);
        const y = currentY + (Math.floor(i / 2) * 25);
        
        doc.setDrawColor(COLORS.BORDER);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, y, kpiBoxWidth, 20, 1, 1, 'S');
        
        doc.setFontSize(8);
        doc.setTextColor(COLORS.TEXT_MUTED);
        doc.text(kpi.label, x + 5, y + 7);
        
        doc.setFontSize(14);
        doc.setTextColor(kpi.color);
        doc.text(kpi.value, x + 5, y + 15);
    });

    currentY += 75;

    // --- PAGE 3: DETAILED FINDINGS ---
    if (data.issues && data.issues.length > 0) {
        doc.addPage();
        doc.setFontSize(18);
        doc.setTextColor(COLORS.PRIMARY);
        doc.text('3. DETAILED FINDINGS', margin, 30);

        const tableBody = (data.issues || []).map(issue => [
            issue.severity.toUpperCase(),
            issue.file.split('/').pop() || 'unknown',
            `L${issue.line}`,
            issue.message
        ]);

        doc.autoTable({
            startY: 40,
            head: [['SEVERITY', 'FILE', 'LINE', 'MESSAGE']],
            body: tableBody,
            margin: { left: margin, right: margin },
            headStyles: {
                fillColor: COLORS.PRIMARY,
                textColor: WHITE,
                fontSize: 9,
                font: 'helvetica',
                fontStyle: 'bold'
            },
            bodyStyles: {
                fontSize: 8,
                textColor: COLORS.TEXT_MAIN
            },
            columnStyles: {
                0: { cellWidth: 25, fontStyle: 'bold' },
                1: { cellWidth: 35 },
                2: { cellWidth: 15 },
                3: { cellWidth: 'auto' }
            },
            didParseCell: (data: any) => {
                if (data.section === 'body' && data.column.index === 0) {
                    const sev = data.cell.raw;
                    if (sev === 'CRITICAL' || sev === 'HIGH') data.cell.styles.textColor = COLORS.ERROR;
                    if (sev === 'MEDIUM') data.cell.styles.textColor = COLORS.WARNING;
                }
            }
        });
    }

    // --- PAGE 4: AI SEMANTIC INSIGHTS ---
    const summaryInsights = data.summary;
    if (summaryInsights) {
        doc.addPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(COLORS.PRIMARY);
        doc.text('4. AI SEMANTIC ANALYSIS', margin, 30);
        
        let aiY = 45;
        // Split by the delimiter used in the backend
        const sections = summaryInsights.split('###').filter(Boolean);
        
        sections.forEach((section) => {
            const lines = section.trim().split('\n');
            const fileName = (lines[0] || 'Unknown File').trim();
            const content = lines.slice(1).join('\n').trim();
            
            // Check if we need a new page
            if (aiY > 250) {
                doc.addPage();
                aiY = 30;
            }
            
            // File Header
            doc.setFillColor(COLORS.BG_LIGHT);
            doc.rect(margin, aiY - 5, contentWidth, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(COLORS.SECONDARY);
            doc.text(`FILE: ${fileName}`, margin + 2, aiY);
            aiY += 8;
            
            // Insight Content
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(COLORS.TEXT_MAIN);
            const splitInsight = doc.splitTextToSize(content, contentWidth - 5);
            doc.text(splitInsight, margin + 2, aiY);
            aiY += (splitInsight.length * 5) + 12;
        });
    }

    // Recommendations Page (Moved to 5)
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(COLORS.PRIMARY);
    doc.text('5. ARCHITECTURAL RECOMMENDATIONS', margin, 30);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.TEXT_MAIN);
    
    let recY = 45;
    const recommendations = [
        { title: 'Prioritize Critical Security Fixes', text: 'Immediate attention is required for files with identified vulnerabilities to prevent potential exploits.' },
        { title: 'Reduce Technical Debt', text: `The current debt is ${data.metrics.technicalDebt}. Refactoring complex modules will improve long-term maintainability.` },
        { title: 'Increase Test Coverage', text: `With ${data.metrics.coverage}% coverage, mission-critical paths may be untested. Targeted unit tests are recommended.` },
        { title: 'Semantic Improvements', text: 'Follow established architectural patterns (Clean Architecture/SOLID) as suggested in the individual file findings.' }
    ];

    recommendations.forEach(rec => {
        doc.setFont('helvetica', 'bold');
        doc.text(`• ${rec.title}`, margin, recY);
        doc.setFont('helvetica', 'normal');
        const splitRec = doc.splitTextToSize(rec.text, contentWidth - 10);
        doc.text(splitRec, margin + 5, recY + 6);
        recY += (splitRec.length * 6) + 10;
    });

    // Final Footer on all pages (except cover)
    const pageCount = doc.getNumberOfPages();
    for (let i = 2; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(COLORS.BORDER);
        doc.line(margin, 285, pageWidth - margin, 285);
        doc.setFontSize(8);
        doc.setTextColor(COLORS.TEXT_MUTED);
        doc.text(`DEVLINT TECHNICAL REPORT | ${data.project.name.toUpperCase()} | PAGE ${i} OF ${pageCount}`, margin, 290);
    }

    return doc.output('blob');
};

const WHITE = '#FFFFFF';
